import React, { useCallback, useState } from 'react';
import { Box } from '@mui/material';
import { AppNoFeedback } from '../components/app/appNoFeedback';
import { Employee } from '../components/employees/employee';
import { ToolboxEmployees } from '../components/employees/employeesToolbox';
import { AppMain } from '../layout/appMain';
import { Employee as EmployeeModel, EmployeeRole } from '../models/employee';
import {
  deleteEmployee,
  GetEmployeesParams,
} from '../services/api/employeeServices';
import { AppConfirmationDelete } from '../components/app/confirmation/appConfiramationDelete';
import { AppConfirmationSuccessful } from '../components/app/confirmation/appConfirmationSuccessful';
import EmployeeDetails from '../components/employees/employeeDetails';
import EmployeeAddRoleDialog from '../components/employees/employeeAddRoleDialog';
import { selectUserRoles } from '../store/profileSlice';
import { FieldValues } from 'react-hook-form';

import AppLoading from '../components/app/appLoading';
import { useAppSelector } from '../hooks';
import { useGetEmployees } from '../hooks/useGetEmployees';
import AppIntersectionObserver from '../components/app/appIntersectionObserver';
import { DEFAULT_PAGE_SIZE } from '../constants';

export enum EmployeesCategory {
  ALL = 'All',
  HIGH_STAFF = 'Admins',
  PROJECT_MANAGERS = 'Project Managers',
  TEAM_LEADERS = 'Team Leaders',
  ENGINEERS = 'Engineers',
  INACTIVE = 'Inactive',
  ACTIVE = 'Active',
}

const style = {
  box: {
    mt: 4,
    display: 'flex',
    flexWrap: 'wrap',
  },
} as const;

export function PageEmployees(): JSX.Element {
  const userRole = useAppSelector(selectUserRoles);

  const [params, setParams] = useState<GetEmployeesParams>({
    filter: '',
    teamLeadGuid: '',
    employeeType: '',
    department: '',
    includeInactive: false,
    includeDeleted: false,
    skip: 0,
    roleId: '',
  });

  const [selectedEmployee, setSelectedEmployee] =
    useState<EmployeeModel | null>(null);
  const [isOpenRolesDialog, setIsOpenRolesDialog] = useState<boolean>(false);
  const [isOpenDetailsDialog, setIsOpenDetailsDialog] =
    useState<boolean>(false);
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState<boolean>(false);
  const [isOpenConfirmationDialog, setIsOpenConfirmationDialog] =
    useState<boolean>(false);

  const { employees, isLoading, refetch, isEmpty, hasMore, updateEmployees } =
    useGetEmployees();

  const handleObserver = useCallback(() => {
    if (isLoading || !hasMore) {
      return;
    }

    const newParams = {
      ...params,
      skip: (params.skip || 0) + DEFAULT_PAGE_SIZE,
    };

    refetch(newParams);
    setParams(newParams);
  }, [hasMore, isLoading, params, refetch]);

  const handleSubmit = useCallback(
    (data: FieldValues) => {
      const {
        filter,
        teamLeadGuid,
        isActive,
        department,
        employeeType,
        roleId,
      } = data;
      const id = teamLeadGuid?.id || '';
      const departmentName = department?.label || department || '';
      const contractTypeValue = employeeType?.value || '';
      const roleFilterId = roleId?.value || '';

      const newParams = {
        ...params,
        filter,
        department: departmentName,
        employeeType: contractTypeValue,
        teamLeadGuid: id,
        roleId: roleFilterId,
        includeInactive: isActive,
        includeDeleted: isActive,
        skip: 0,
      };
      setParams(newParams);

      refetch(newParams);
    },
    [params, refetch]
  );

  const handleClose = useCallback(() => {
    setIsOpenRolesDialog(false);
    setIsOpenDetailsDialog(false);
  }, []);

  const handleRoles = useCallback((employee) => {
    setSelectedEmployee(employee);
    setIsOpenRolesDialog(true);
  }, []);

  const handleDetails = useCallback((employee) => {
    setIsOpenDetailsDialog(true);
    setSelectedEmployee(employee);
  }, []);

  const updateEmployee = useCallback(
    (employeeId: string, workingHours: number) => {
      const updatedEmployees = employees.map((employee) => {
        if (employee.id === employeeId) {
          return {
            ...employee,
            workingHours,
          };
        }

        return employee;
      });

      updateEmployees(updatedEmployees);
    },
    [employees, updateEmployees]
  );

  const handleDelete = useCallback((employee) => {
    setIsOpenDeleteDialog(true);
    setSelectedEmployee(employee);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setIsOpenDeleteDialog(false);
  }, []);

  const handleCloseConfirmationDialog = useCallback(() => {
    setIsOpenConfirmationDialog(false);
  }, []);

  const handleConfirmDeleteEmployee = useCallback(() => {
    if (selectedEmployee)
      deleteEmployee(selectedEmployee.id)
        .then(() => {
          handleCloseDeleteDialog();
          setIsOpenConfirmationDialog(true);
        })
        .then(() => {
          setTimeout(() => {
            handleCloseConfirmationDialog();
            refetch(params);
          }, 500);
        });
  }, [
    selectedEmployee,
    handleCloseDeleteDialog,
    handleCloseConfirmationDialog,
    refetch,
    params,
  ]);

  const updateEmployeePermissionGroups = useCallback(
    (employeeId: string, permissionGroups: EmployeeRole[]) => {
      const updatedEmployees = employees.map((employee) => {
        if (employee.id === employeeId) {
          return {
            ...employee,
            permissionGroups,
          };
        }

        return employee;
      });

      updateEmployees(updatedEmployees);
    },
    [employees, updateEmployees]
  );

  return (
    <AppMain>
      <ToolboxEmployees handleSubmit={handleSubmit} />

      <Box sx={style.box}>
        {isEmpty ? (
          <AppNoFeedback info="employee" />
        ) : (
          employees.map((employee) => {
            return (
              <Employee
                employee={employee}
                key={employee.id}
                handleRoles={handleRoles}
                handleDetails={handleDetails}
                handleDelete={handleDelete}
                permissions={userRole}
              />
            );
          })
        )}
        <AppIntersectionObserver intersect={handleObserver} />
      </Box>
      {isLoading && <AppLoading />}

      {isOpenRolesDialog && (
        <EmployeeAddRoleDialog
          updateEmployeePermissionGroups={updateEmployeePermissionGroups}
          onClose={handleClose}
          isOpen={isOpenRolesDialog}
          employee={selectedEmployee}
        />
      )}

      <EmployeeDetails
        isOpen={isOpenDetailsDialog}
        onClose={handleClose}
        employee={selectedEmployee}
        updateEmployee={updateEmployee}
      />

      <AppConfirmationDelete
        isOpen={isOpenDeleteDialog}
        message={`Delete employee ${selectedEmployee?.name}?`}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDeleteEmployee}
      />

      <AppConfirmationSuccessful
        isOpen={isOpenConfirmationDialog}
        message="Sucessfully Deleted!"
        onClose={handleCloseConfirmationDialog}
      />
    </AppMain>
  );
}
